import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { FolderTemplate, FolderTemplateNode } from '@/types/api';

export function useFolderTemplates() {
  return useQuery({
    queryKey: ['folder-templates'],
    queryFn: async () =>
      (await api.get<FolderTemplate[]>('/admin/folder-templates')).data,
  });
}

export function useCreateFolderTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      name: string;
      description?: string;
      structure: FolderTemplateNode[];
    }) => {
      const { data } = await api.post<FolderTemplate>('/admin/folder-templates', vars);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folder-templates'] }),
  });
}

export function useUpdateFolderTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      name?: string;
      description?: string;
      structure?: FolderTemplateNode[];
    }) => {
      const { id, ...rest } = vars;
      const { data } = await api.patch<FolderTemplate>(`/admin/folder-templates/${id}`, rest);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folder-templates'] }),
  });
}

export function useDeleteFolderTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/folder-templates/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folder-templates'] }),
  });
}

export function useApplyFolderTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { templateId: string; workspaceId: string }) => {
      const { data } = await api.post<{ folders_created: number; template_id: string }>(
        `/admin/folder-templates/${vars.templateId}/apply/${vars.workspaceId}`,
      );
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['folders', vars.workspaceId] });
    },
  });
}
